import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { supabase } from './services/supabase'
import errorTracker from './services/errorTracking'
import reloadDebugger from './utils/reloadDebugger'
// Performance monitoring removed - was causing page reloads
import { OrganizationProvider } from './contexts/OrganizationContext'
import { MasterAdminProvider } from './contexts/MasterAdminContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { ToastProvider } from './contexts/ToastContext'
import { LanguageProvider } from './contexts/LanguageContext'
import AuthPage from './pages/AuthPage'
import Dashboard from './pages/Dashboard'
import Clients from './pages/Clients'
import ClientDetails from './pages/ClientDetails'
import Examination from './pages/Examination'
import CarePlans from './pages/CarePlans'
import CarePlanDetails from './pages/CarePlanDetails'
import Therapies from './pages/Therapies'
import Sessions from './pages/Sessions'
import OrganizationSettings from './pages/OrganizationSettings'
import InviteTeamMember from './pages/InviteTeamMember'
import Invitations from './pages/Invitations'
import OnboardingPage from './pages/OnboardingPage'
import OnboardingAPI from './pages/OnboardingAPI'
import UserSetup from './pages/UserSetup'
import SecuritySettings from './pages/SecuritySettings'
import BackupRestore from './pages/BackupRestore'
import GDPRManagement from './pages/GDPRManagement'
import ComplianceDashboard from './pages/ComplianceDashboard'
import ErrorDashboard from './pages/ErrorDashboard'
import MasterAdminDashboard from './pages/MasterAdminDashboard'
import MasterAdminOrganizations from './pages/MasterAdminOrganizations'
import MasterAdminOrganizationDetails from './pages/MasterAdminOrganizationDetails'
import MasterAdminFeatureManagement from './pages/MasterAdminFeatureManagement'
import MasterAdminStorageLimits from './components/MasterAdminStorageLimits'
import MasterAdminOrganizationRequests from './pages/MasterAdminOrganizationRequests'
import RevenueAnalytics from './pages/RevenueAnalytics'
import ReportBuilder from './components/ReportBuilder'
import PracticePerformance from './pages/PracticePerformance'
import ClientOutcomes from './pages/ClientOutcomes'
import ClientFollowups from './pages/ClientFollowups'
import BillingManagement from './pages/BillingManagement'
import LegalDocuments from './pages/LegalDocuments'
import Profile from './pages/Profile'
import ResetPassword from './pages/ResetPassword'
import Support from './pages/Support'
import Forum from './pages/Forum'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import ProtectedRoute from './components/ProtectedRoute'
import MasterAdminProtectedRoute from './components/MasterAdminProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import SessionSecurityProvider from './components/SessionSecurityProvider'
import NavigationTracker from './components/NavigationTracker'

function App() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Initialize error tracking
    // Performance monitoring removed - was causing page reloads
    
    // Make available globally for API wrapper
    window.errorTracker = errorTracker
    
    // Set up global error handlers
    const handleGlobalError = (event) => {
      errorTracker.captureException(event.error || new Error(event.message), {
        type: 'unhandledError',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    }

    const handleUnhandledRejection = (event) => {
      errorTracker.captureException(event.reason || new Error('Unhandled promise rejection'), {
        type: 'unhandledRejection',
        promise: true,
      })
    }

    window.addEventListener('error', handleGlobalError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      // Store initial session for reload debugger to detect session restorations
      if (session?.user?.id) {
        sessionStorage.setItem('lastAuthSession', JSON.stringify({
          userId: session.user.id,
          timestamp: Date.now(),
          event: 'INITIAL_LOAD',
        }))
      }
      
      // Set user context for error tracking
      if (session?.user) {
        errorTracker.setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name,
        })
      }
    })

    // Listen for changes on auth state
    // Only react to actual auth changes, not token refreshes (which happen on visibility change)
    let lastKnownUserId = null
    
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Track auth state changes for debugging
      reloadDebugger.trackAuthStateChange(event, session)
      
      const currentUserId = session?.user?.id
      
      // Detect if this is a duplicate SIGNED_IN for the same user (session restoration)
      const isDuplicateSignIn = event === 'SIGNED_IN' && 
        lastKnownUserId === currentUserId && 
        currentUserId !== null
      
      // Only update user state on actual auth events, not on TOKEN_REFRESHED or duplicate SIGNED_IN
      // TOKEN_REFRESHED happens automatically and shouldn't cause state changes
      // Duplicate SIGNED_IN happens when Supabase restores session from storage
      if (event !== 'TOKEN_REFRESHED' && !isDuplicateSignIn) {
        setUser(session?.user ?? null)
        lastKnownUserId = currentUserId
        
        // Update user context
        if (session?.user) {
          errorTracker.setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.name,
          })
        } else {
          errorTracker.setUser(null)
          lastKnownUserId = null
        }
      } else if (isDuplicateSignIn) {
        // Log that we're ignoring a duplicate SIGNED_IN
        console.debug('[Auth] Ignoring duplicate SIGNED_IN event for existing session')
      }
    })

    // Note: We don't prevent visibility changes - React handles component lifecycle
    // The reload issue is likely due to React.StrictMode in development or browser behavior
    // We just ensure state is preserved via localStorage/sessionStorage

    return () => {
      subscription.unsubscribe()
      window.removeEventListener('error', handleGlobalError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <ToastProvider>
            <Router>
            <NavigationTracker />
            <Routes>
        <Route
          path="/login"
          element={user ? <Navigate to="/dashboard" /> : <AuthPage />}
        />
        <Route
          path="/register"
          element={user ? <Navigate to="/dashboard" /> : <AuthPage />}
        />
        <Route
          path="/onboarding"
          element={user ? <Navigate to="/dashboard" /> : <OnboardingPage />}
        />
        <Route
          path="/onboarding-api"
          element={<OnboardingAPI />}
        />
        <Route
          path="/reset-password"
          element={<ResetPassword />}
        />
        {/* Public legal document routes - accessible without authentication */}
        <Route
          path="/terms"
          element={<Navigate to="/legal/terms" replace />}
        />
        <Route
          path="/privacy"
          element={<Navigate to="/legal/privacy" replace />}
        />
        <Route
          path="/legal/:documentType"
          element={<LegalDocuments />}
        />
        <Route
          path="/setup"
          element={
            user ? (
              <MasterAdminProvider>
                <OrganizationProvider>
                  <UserSetup />
                </OrganizationProvider>
              </MasterAdminProvider>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
              <Route
                path="/*"
                element={
                  user ? (
                    <MasterAdminProvider>
                      <OrganizationProvider>
                        <SessionSecurityProvider>
                          <Layout>
                            <Routes>
                      {/* Master Admin Routes */}
                      <Route
                        path="/master-admin"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminDashboard />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/organizations"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminOrganizations />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/organizations/:id"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminOrganizationDetails />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/storage-limits"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminStorageLimits />
                          </MasterAdminProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/organization-requests"
                        element={
                          <MasterAdminProtectedRoute>
                            <MasterAdminOrganizationRequests />
                          </MasterAdminProtectedRoute>
                        }
                      />

                      {/* Regular App Routes */}
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route
                        path="/clients"
                        element={
                          <ProtectedRoute resource="clients" action="read">
                            <Clients />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/:id"
                        element={
                          <ProtectedRoute resource="clients" action="read">
                            <ClientDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/examination"
                        element={
                          <ProtectedRoute resource="clients" action="write">
                            <Examination />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/care-plans"
                        element={
                          <ProtectedRoute resource="care_plans" action="read">
                            <CarePlans />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/care-plans/:id"
                        element={
                          <ProtectedRoute resource="care_plans" action="read">
                            <CarePlanDetails />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/therapies"
                        element={
                          <ProtectedRoute resource="therapies" action="read">
                            <Therapies />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/sessions"
                        element={
                          <ProtectedRoute resource="sessions" action="read">
                            <Sessions />
                          </ProtectedRoute>
                        }
                      />
                    <Route
                      path="/organization"
                      element={
                        <ProtectedRoute requireAdmin>
                          <OrganizationSettings />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/organization/invite"
                      element={
                        <ProtectedRoute requireAdmin>
                          <InviteTeamMember />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/invitations"
                      element={
                        <ProtectedRoute>
                          <Invitations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/security"
                      element={
                        <MasterAdminProtectedRoute>
                          <SecuritySettings />
                        </MasterAdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/backup-restore"
                      element={
                        <ProtectedRoute requireAdmin>
                          <BackupRestore />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/gdpr"
                      element={
                        <MasterAdminProtectedRoute>
                          <GDPRManagement />
                        </MasterAdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/errors"
                      element={
                        <MasterAdminProtectedRoute>
                          <ErrorDashboard />
                        </MasterAdminProtectedRoute>
                      }
                    />
                    <Route
                      path="/support"
                      element={
                        <ProtectedRoute>
                          <Support />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/forum"
                      element={
                        <ProtectedRoute>
                          <Forum />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/onboarding-api"
                      element={<OnboardingAPI />}
                    />
                    <Route
                      path="/compliance"
                      element={
                        <ProtectedRoute requireAdmin>
                          <ComplianceDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/reports"
                      element={
                        <ProtectedRoute>
                          <ReportBuilder />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/performance"
                      element={
                        <ProtectedRoute>
                          <PracticePerformance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/outcomes"
                      element={
                        <ProtectedRoute>
                          <ClientOutcomes />
                        </ProtectedRoute>
                      }
                    />
                      <Route
                        path="/followups"
                        element={
                          <ProtectedRoute>
                            <ClientFollowups />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/billing"
                        element={
                          <ProtectedRoute>
                            <BillingManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/master-admin/revenue"
                        element={
                          <MasterAdminProtectedRoute>
                            <RevenueAnalytics />
                          </MasterAdminProtectedRoute>
                        }
                      />
                            <Route path="/app" element={<Navigate to="/dashboard" />} />
                          </Routes>
                        </Layout>
                        </SessionSecurityProvider>
                      </OrganizationProvider>
                    </MasterAdminProvider>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
        </Router>
        </ToastProvider>
      </ThemeProvider>
      </LanguageProvider>
      </ErrorBoundary>
    )
  }
  
  export default App

